import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, normalizeClass as _normalizeClass, unref as _unref, withCtx as _withCtx, createBlock as _createBlock, normalizeStyle as _normalizeStyle, createVNode as _createVNode } from "vue"

const _hoisted_1 = { class: "space-y-2" }
const _hoisted_2 = {
  key: 0,
  class: "text-normal text-gray-500"
}
const _hoisted_3 = { class: "truncate text-sm text-gray-500" }
const _hoisted_4 = { class: "ml-1 text-xs font-normal text-gray-500" }
const _hoisted_5 = ["title"]
const _hoisted_6 = { class: "truncate text-sm text-gray-500" }
const _hoisted_7 = { class: "ml-1 text-xs font-normal text-gray-500" }
const _hoisted_8 = {
  key: 1,
  class: "mt-1.5 h-1.5 w-16 overflow-hidden rounded bg-gray-200"
}
const _hoisted_9 = { class: "min-w-0 px-4 py-3" }
const _hoisted_10 = { class: "truncate text-sm text-gray-500" }
const _hoisted_11 = { class: "flex flex-wrap items-baseline gap-x-1" }
const _hoisted_12 = {
  key: 1,
  class: "text-xl font-semibold text-gray-400",
  "aria-hidden": "true"
}
const _hoisted_13 = { class: "whitespace-nowrap text-xs text-gray-500 tabular-nums" }
const _hoisted_14 = { class: "whitespace-nowrap text-sm text-gray-700" }
const _hoisted_15 = {
  key: 1,
  class: "text-xl font-semibold text-gray-400"
}

import { computed } from 'vue';
import { parseISO } from 'date-fns';
import MainViewCard from '@/components/common/MainViewCard.vue';
import { Badge } from '@/components/ui/badge';
import { lotFraction, lotTitle, countPercent, formatPercent, formatQuantity, PERCENT_TONE_CLASSES, PERCENT_TONE_TEXT_CLASSES, percentTone, signedNumber, signTone } from '@/composables/inventory/inventoryTable';
import { useInventoryScope } from '@/composables/inventory/useInventoryScope';
import { i18n } from '@/plugins/internationalization/i18n';
import type { MedicineInventoryRow } from '@/types';


export default /*@__PURE__*/_defineComponent({
  __name: 'PeriodFigures',
  props: {
    detail: {}
  },
  setup(__props: any) {

const props = __props;

const { periodRange } = useInventoryScope();

const label = (key: string) => i18n.global.t(`views.health.inventory.table.headings.${key}`);
const formatDate = (date: string) => i18n.global.d(parseISO(date), 'short');

/** Nothing adjusted reads quiet; a shortfall found at a count is red, a surplus green. */
function adjustmentTone(adjustment: number): string {
    return adjustment === 0 ? 'text-gray-400' : signTone(adjustment);
}

/** The period's figures, one card each; the balance closes the row. */
const figures = computed(() => {
    const { opening, bought, used, wasted, adjustment } = props.detail;

    return [
        { id: 'opening', label: label('opening'), value: formatQuantity(opening), tone: '' },
        { id: 'bought', label: label('bought'), value: formatQuantity(bought), tone: '' },
        { id: 'used', label: label('used'), value: formatQuantity(used), tone: '' },
        { id: 'wasted', label: label('wasted'), value: formatQuantity(wasted), tone: '' },
        { id: 'adjustment', label: label('adjustment'), value: signedNumber(adjustment, formatQuantity), tone: adjustmentTone(adjustment) },
    ];
});

const lastCountPercent = computed(() => countPercent(props.detail.last_count));

/** What the last count was off by, in the key's unit; "matches" when it found what was expected. */
const lastCountDifference = computed(() => {
    const { last_count: lastCount, unit_name: unitName } = props.detail;

    if (lastCount === null) {
        return '';
    }

    return lastCount.difference === 0 ? i18n.global.t('views.health.inventory.chips.countMatches') : `${signedNumber(lastCount.difference, formatQuantity)} ${unitName}`;
});

const period = computed(() => {
    const { from, to } = periodRange.value;

    return from && to ? i18n.global.t('views.health.inventory.detail.period', { from: formatDate(from), to: formatDate(to) }) : '';
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    (period.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_2, _toDisplayString(period.value), 1))
      : _createCommentVNode("", true),
    _createVNode(MainViewCard, { class: "grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 divide-gray-100 max-lg:gap-y-1 lg:divide-x" }, {
      default: _withCtx(() => [
        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(figures.value, (figure) => {
          return (_openBlock(), _createElementBlock("div", {
            key: figure.id,
            class: "min-w-0 px-4 py-3"
          }, [
            _createElementVNode("div", _hoisted_3, _toDisplayString(figure.label), 1),
            _createElementVNode("div", {
              class: _normalizeClass(["text-xl font-semibold whitespace-nowrap tabular-nums", figure.tone])
            }, [
              _createTextVNode(_toDisplayString(figure.value), 1),
              _createElementVNode("small", _hoisted_4, _toDisplayString(_ctx.detail.unit_name), 1)
            ], 2)
          ]))
        }), 128)),
        _createElementVNode("div", {
          class: "min-w-0 px-4 py-3",
          title: _unref(lotTitle)(_ctx.detail)
        }, [
          _createElementVNode("div", _hoisted_6, _toDisplayString(label('closing')), 1),
          _createElementVNode("div", {
            class: _normalizeClass(["text-xl font-bold whitespace-nowrap tabular-nums", { 'text-red-600': _ctx.detail.closing < 0 }])
          }, [
            _createTextVNode(_toDisplayString(_unref(formatQuantity)(_ctx.detail.closing)), 1),
            _createElementVNode("small", _hoisted_7, _toDisplayString(_ctx.detail.unit_name), 1)
          ], 2),
          (!_ctx.detail.has_purchase)
            ? (_openBlock(), _createBlock(_unref(Badge), {
                key: 0,
                variant: "outline",
                class: _normalizeClass(["mt-1 whitespace-nowrap font-medium", _unref(PERCENT_TONE_CLASSES).error])
              }, {
                default: _withCtx(() => [
                  _createTextVNode(_toDisplayString(_ctx.$t('views.health.inventory.chips.consumptionWithoutPurchase')), 1)
                ]),
                _: 1
              }, 8, ["class"]))
            : (_openBlock(), _createElementBlock("div", _hoisted_8, [
                _createElementVNode("div", {
                  class: "h-full rounded bg-primary-700",
                  style: _normalizeStyle({ width: `${_ctx.detail.active_lot ? _unref(lotFraction)(_ctx.detail.active_lot) * 100 : 0}%` })
                }, null, 4)
              ]))
        ], 8, _hoisted_5),
        _createElementVNode("div", _hoisted_9, [
          _createElementVNode("div", _hoisted_10, _toDisplayString(label('lastCount')), 1),
          (_ctx.detail.last_count)
            ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                _createElementVNode("div", _hoisted_11, [
                  (lastCountPercent.value !== null)
                    ? (_openBlock(), _createElementBlock("span", {
                        key: 0,
                        class: _normalizeClass(["text-xl font-semibold whitespace-nowrap tabular-nums", _unref(PERCENT_TONE_TEXT_CLASSES)[_unref(percentTone)(lastCountPercent.value)]])
                      }, _toDisplayString(_unref(formatPercent)(lastCountPercent.value)), 3))
                    : (_openBlock(), _createElementBlock("span", _hoisted_12, "–")),
                  _createElementVNode("span", _hoisted_13, "(" + _toDisplayString(lastCountDifference.value) + ")", 1)
                ]),
                _createElementVNode("div", _hoisted_14, _toDisplayString(formatDate(_ctx.detail.last_count.date)), 1)
              ], 64))
            : (_openBlock(), _createElementBlock("div", _hoisted_15, _toDisplayString(_ctx.$t('views.health.inventory.chips.noCount')), 1))
        ])
      ]),
      _: 1
    })
  ]))
}
}

})